import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ColumnSelect } from "./ui/select";
import { Search, Filter, ChevronLeft, ChevronRight, BarChart3, Send, Pencil, RotateCcw, XCircle, Trash2, Megaphone, Clock, CheckCircle, PauseCircle, FileEdit, AlertCircle, Hourglass, Eye, Plus, Zap, Calendar, MessageSquare, ChevronDown, ChevronUp, Mail, Play, Sparkles } from "lucide-react";
import { api, useAuth } from "@/contexts/AuthContext";
import RenewalDetailsModal from "./renewals/renewalDetailsModal";
import RenewalDeleteConfirm from "./renewals/renewalDeleteConfirm";
import RenewalCreate from "./renewals/renewalCreate";
import { usePlanStatus } from "@/hooks/usePlanStatus";
import {
  useMetaAccountHealth,
  fetchConnectedAccounts,
  hasActiveWhatsAppAccount,
} from "@/hooks/useMetaAccountHealth";
import MetaAccountHealthBanner from "./MetaAccountHealthBanner";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import TemplatePreviewPanel from "./TemplatePreviewPanel";
import {
  buildTemplatesMap,
  resolveTemplatePreview,
  formatTemplateName,
  type TemplateMapEntry,
} from "@/lib/templatePreview";
import { invalidateAuthenticatedResource } from "@/lib/authenticatedResource";
import { fetchAllCampaignLists } from "@/lib/campaignsListApi";

type Periodicity = { value: number; unit: "MONTHS" | "YEARS" };
type RenewalCampaignItem = {
  id: string;
  name: string;
  status: "active" | "paused" | "draft" | "completed" | "cancelled" | "pending_template_approval" | "template_rejected";
  created_at: string;
  updated_at?: string;
  message_template?: string;
  template_name?: string;
  whatsapp_template_name?: string;
  template_id?: string;
  template_status?: string;
  google_sheet_id?: string;  // ✅ NUEVO: ID de la hoja para resolver nombre
  google_sheet_name?: string;
  renewal_config?: { periodicity?: Periodicity; reminder_days_before?: number[] };
  messages_sent?: number;
  messages_delivered?: number;
  messages_failed?: number;  // ✅ NUEVO: Mensajes fallidos
  delivery_rate?: number;
  total_recipients?: number;
  messages_cancelled?: number;
  // Nueva propiedad para distinguir tipo de campaña
  campaignType?: "marketing" | "renewal" | "dynamic" | "calendar";
  calendar_id?: string;
};

const isPauseableType = (type?: RenewalCampaignItem["campaignType"]) =>
  type === "renewal" || type === "dynamic" || type === "calendar";

const lifecycleBasePath = (type?: string) => {
  if (type === "dynamic") return "/dynamics";
  if (type === "calendar") return "/calendar-campaigns";
  return "/renewals";
};

const statusConfig: Record<string, { label: string; color: string; description?: string }> = {
  active: { label: "Activa", color: "bg-green-100 text-green-800 border-green-200" },
  paused: { label: "Pausada", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  draft: { label: "Borrador", color: "bg-gray-100 text-gray-800 border-gray-200" },
  scheduled: { label: "Programada", color: "bg-blue-100 text-blue-800 border-blue-200" },
  sent: { label: "Enviada", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  completed: { label: "Completada", color: "bg-blue-100 text-blue-800 border-blue-200" },
  completed_with_errors: { label: "Completada", color: "bg-blue-100 text-blue-800 border-blue-200" },  // ✅ Mismo label que completed
  failed: { label: "Fallida", color: "bg-red-100 text-red-800 border-red-200" },
  cancelled: { label: "Cancelada", color: "bg-red-100 text-red-800 border-red-200" },
  pending_template_approval: {
    label: "Pendiente de Activación",
    color: "bg-orange-100 text-orange-800 border-orange-200",
    description: "Esperando aprobación de la plantilla por Meta"
  },
  template_rejected: {
    label: "Plantilla Rechazada",
    color: "bg-red-100 text-red-800 border-red-200",
    description: "La plantilla fue rechazada por Meta"
  },
};

const LORO_FROM = "from-[#1F4068]";
const LORO_TO = "to-[#1CB3B3]";

const RenewalCampaigns: React.FC<{ onCreateNew?: () => void }> = ({ onCreateNew }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // ✅ NUEVO: Obtener usuario para notificaciones
  const { user } = useAuth();
  // ✅ NUEVO: Estado para colapsar información del plan (inicia colapsado)
  const [isPlanExpanded, setIsPlanExpanded] = useState(false);

  const [highlightCampaignId, setHighlightCampaignId] = useState<string | null>(null);

  const [items, setItems] = useState<RenewalCampaignItem[]>([]);
  const [showCreate, setShowCreate] = useState<boolean>(false);
  // Ocultar permanentemente el botón de crear campaña después del primer clic
  const [hideCreateButton, setHideCreateButton] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>(""); // '' = Todas, 'marketing' = Marketing, 'renewal' = Renovación
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const filtersRef = useRef<HTMLDivElement | null>(null);
  /** Bumps on each load() / unmount so stale background enrich cannot setState. */
  const loadGenerationRef = useRef(0);
  const perPage = 6;

  // ✅ Hook para obtener información del plan
  const { planStatus, loading: loadingPlan, error: planError, loadPlanStatus, getPlanDisplayName } = usePlanStatus();
  const { accounts: whatsappAccounts, criticalAccounts } = useMetaAccountHealth();

  // ✅ Mapa de templates de Meta para enriquecer visualización de campañas
  const [metaTemplatesMap, setMetaTemplatesMap] = useState<Record<string, TemplateMapEntry>>({});

  // ✅ Pre-cached assets para el wizard de creación
  const [preCachedAssets, setPreCachedAssets] = useState<{
    sheets: any[];
    templates: any[];
    metaTemplates: any[];
  } | null>(null);

  const isLoadStale = (generation: number) => generation !== loadGenerationRef.current;

  // ✅ Enriquecer vista de campañas (templates locales + Meta). Solo aplica state si generation sigue vigente.
  const fetchMetaTemplates = async (opts?: {
    hasActiveWhatsApp?: boolean;
    generation?: number;
  }) => {
    try {
      let localTemplates: any[] = [];
      try {
        const localRes = await api.get("/templates");
        localTemplates = Array.isArray(localRes.data) ? localRes.data : localRes.data?.data || [];
      } catch (localErr) {
        console.warn("Could not fetch local templates for fallback", localErr);
      }

      let metaTemplates: any[] = [];
      const canCallMeta = opts?.hasActiveWhatsApp ?? whatsappAccounts.some((a) => a.status === "active");
      if (canCallMeta) {
        try {
          const response = await api.get("/whatsapp/templates/meta/list");
          if (response.data.success && response.data.templates) {
            metaTemplates = response.data.templates;
          }
        } catch (metaErr) {
          console.warn("Could not fetch Meta templates for enrichment", metaErr);
        }
      }

      if (opts?.generation !== undefined && isLoadStale(opts.generation)) return;
      setMetaTemplatesMap(buildTemplatesMap(localTemplates, metaTemplates));
    } catch (error) {
      console.error("Error fetching meta templates:", error);
    }
  };

  useEffect(() => {
    return () => {
      loadGenerationRef.current += 1;
    };
  }, []);

  // ✅ Verificar si WhatsApp está conectado (reusa caché de sesión)
  const checkWhatsAppConnection = async (): Promise<boolean> => {
    try {
      const accounts = await fetchConnectedAccounts();
      return hasActiveWhatsAppAccount(accounts);
    } catch (error) {
      console.error("Error checking WhatsApp connection:", error);
      return false;
    }
  };

  // ✅ Pre-cargar assets cuando el usuario hace clic en crear campaña
  const preloadAssets = async () => {
    try {
      const accounts = await fetchConnectedAccounts();
      const canCallMeta = hasActiveWhatsAppAccount(accounts);

      const requests: Promise<any>[] = [
        api.get("/google-sheets"),
        api.get("/templates"),
      ];
      if (canCallMeta) {
        requests.push(api.get("/whatsapp/templates/meta/list"));
      }

      const [sRes, tRes, mRes] = await Promise.allSettled(requests);

      const sheets = sRes.status === "fulfilled" ? (sRes.value.data || []) : [];
      const templates = tRes.status === "fulfilled" ? (tRes.value.data || []) : [];
      const metaTemplates =
        canCallMeta && mRes && mRes.status === "fulfilled" && mRes.value.data?.templates
          ? mRes.value.data.templates
          : [];

      setPreCachedAssets({ sheets, templates, metaTemplates });
      console.log("✅ Assets pre-cargados:", { sheets: sheets.length, templates: templates.length, metaTemplates: metaTemplates.length });
    } catch (err) {
      console.error("Error pre-cargando assets:", err);
    }
  };

  // Delete modal state (igual que CampaignList)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name?: string } | null>(null);

  // ✅ NUEVO: Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [cancelTarget, setCancelTarget] = useState<{ id: string; name?: string; campaignType?: string } | null>(null);
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [showPauseModal, setShowPauseModal] = useState<boolean>(false);
  const [pauseTarget, setPauseTarget] = useState<{ id: string; name?: string; campaignType?: string } | null>(null);
  const [pausing, setPausing] = useState<boolean>(false);
  const [resumingId, setResumingId] = useState<string | null>(null);

  const openDeleteConfirm = (campaign: RenewalCampaignItem) => {
    setDeleteTarget({ id: campaign.id, name: campaign.name });
    setShowDeleteModal(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  // ✅ NUEVO: Funciones para cancelar campaña
  const openCancelConfirm = (campaign: RenewalCampaignItem) => {
    setCancelTarget({ id: campaign.id, name: campaign.name, campaignType: campaign.campaignType });
    setShowCancelModal(true);
  };

  const closeCancelConfirm = () => {
    setShowCancelModal(false);
    setCancelTarget(null);
  };

  const cancelCampaign = async () => {
    if (!cancelTarget) return;

    setCancelling(true);

    // Determinar el endpoint correcto basado en el tipo de campaña
    let endpoint: string;
    if (cancelTarget.campaignType === "marketing") {
      endpoint = `/campaigns/${cancelTarget.id}/cancel`;
    } else if (cancelTarget.campaignType === "dynamic") {
      endpoint = `/dynamics/${cancelTarget.id}/cancel`;
    } else if (cancelTarget.campaignType === "calendar") {
      endpoint = `/calendar-campaigns/${cancelTarget.id}/cancel`;
    } else {
      endpoint = `/renewals/${cancelTarget.id}/cancel`;
    }

    try {
      const response = await api.post(endpoint);
      const data = response.data as { cancelled_count?: number };

      // Actualizar el estado local de la campaña
      setItems((prev) => prev.map((p) =>
        p.id === cancelTarget.id ? { ...p, status: "cancelled" as const } : p
      ));

      toast.success(`Campaña cancelada. ${data.cancelled_count || 0} mensajes pendientes eliminados.`);
    } catch (e: any) {
      console.error(e);
      const errorMsg = e?.response?.data?.detail || "No se pudo cancelar la campaña";
      toast.error(errorMsg);
    } finally {
      setCancelling(false);
      closeCancelConfirm();
    }
  };

  const openPauseConfirm = (campaign: RenewalCampaignItem) => {
    setPauseTarget({ id: campaign.id, name: campaign.name, campaignType: campaign.campaignType });
    setShowPauseModal(true);
  };

  const closePauseConfirm = () => {
    setShowPauseModal(false);
    setPauseTarget(null);
  };

  const pauseCampaign = async () => {
    if (!pauseTarget) return;
    setPausing(true);
    try {
      const response = await api.post(`${lifecycleBasePath(pauseTarget.campaignType)}/${pauseTarget.id}/pause`);
      const data = response.data as { cancelled_count?: number };
      setItems((prev) => prev.map((p) =>
        p.id === pauseTarget.id ? { ...p, status: "paused" as const } : p
      ));
      toast.success(`Campaña pausada. ${data.cancelled_count || 0} recordatorios pendientes detenidos.`);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "No se pudo pausar la campaña");
    } finally {
      setPausing(false);
      closePauseConfirm();
    }
  };

  const resumeCampaign = async (campaign: RenewalCampaignItem) => {
    setResumingId(campaign.id);
    try {
      await api.post(`${lifecycleBasePath(campaign.campaignType)}/${campaign.id}/resume`);
      setItems((prev) => prev.map((p) =>
        p.id === campaign.id ? { ...p, status: "active" as const } : p
      ));
      toast.success("Campaña reactivada. Se programarán los recordatorios futuros.");
    } catch (e: any) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "No se pudo reactivar la campaña");
    } finally {
      setResumingId(null);
    }
  };

  // ✅ NUEVO: Resolver nombres de Google Sheets para las campañas
  const resolveSheetNames = async (campaigns: RenewalCampaignItem[]): Promise<RenewalCampaignItem[]> => {
    // Recolectar IDs únicos de hojas (siempre, para poder actualizar nombres desactualizados)
    const sheetIds = new Set<string>();
    campaigns.forEach(c => {
      const anyc = c as any;
      const sheetId = anyc.google_sheet_id ?? anyc.googleSheetId ?? anyc.sheet_id;
      if (sheetId && c.campaignType !== "calendar") {
        sheetIds.add(String(sheetId));
      }
    });

    if (sheetIds.size === 0) return campaigns;

    console.log(`📥 Resolviendo ${sheetIds.size} nombres de hojas...`);

    try {
      // Cargar todas las hojas conectadas del usuario
      const sheetsRes = await api.get<any[]>("/google-sheets");
      const sheets = sheetsRes.data || [];

      // Crear mapa de sheetId -> name
      const sheetNameMap: Record<string, string> = {};
      sheets.forEach((s: any) => {
        const id = String(s.id ?? s.sheet_id ?? s._id ?? s.sheetId ?? "");
        const name = s.name ?? s.title ?? s.sheet_name ?? "";
        if (id && name) {
          sheetNameMap[id] = name;
        }
      });

      console.log("✅ Hojas encontradas:", Object.keys(sheetNameMap).length);

      // Enriquecer campañas con nombres de hojas
      return campaigns.map(c => {
        const anyc = c as any;
        const sheetId = String(anyc.google_sheet_id ?? anyc.googleSheetId ?? anyc.sheet_id ?? "");
        if (sheetId && sheetNameMap[sheetId]) {
          return { ...c, google_sheet_name: sheetNameMap[sheetId] };
        }
        return c;
      });
    } catch (err) {
      console.error("Error resolviendo nombres de hojas:", err);
      return campaigns;
    }
  };

  // ✅ NUEVO: Resolver nombres de Google Calendars para campañas calendar
  const resolveCalendarNames = async (campaigns: RenewalCampaignItem[]): Promise<RenewalCampaignItem[]> => {
    const calendarIds = new Set<string>();
    campaigns.forEach(c => {
      const anyc = c as any;
      if (anyc.campaignType === "calendar" && anyc.calendar_id) {
        calendarIds.add(String(anyc.calendar_id));
      }
    });

    if (calendarIds.size === 0) return campaigns;

    try {
      const calRes = await api.get<any>("/calendar/calendars");
      // El backend devuelve { success: true, calendars: [...] }
      const calendars = calRes.data?.calendars || (Array.isArray(calRes.data) ? calRes.data : []);
      const calNameMap: Record<string, string> = {};
      
      calendars.forEach((c: any) => {
        if (c.id && c.summary) {
          calNameMap[c.id] = c.summary;
        }
      });

      return campaigns.map(c => {
        const anyc = c as any;
        if (anyc.campaignType === "calendar" && anyc.calendar_id && calNameMap[anyc.calendar_id]) {
          return { ...c, calendar_name: calNameMap[anyc.calendar_id] };
        }
        return c;
      });
    } catch (err) {
      console.error("Error resolviendo nombres de calendarios:", err);
      return campaigns;
    }
  };

  /**
   * Background enrich: sheet/calendar names + template preview (+ plan on refresh).
   * Does not gate the page spinner; discarded if a newer load() started or component unmounted.
   */
  const enrichCampaignCards = async (
    campaigns: RenewalCampaignItem[],
    generation: number,
    opts: { isRefresh: boolean }
  ) => {
    try {
      if (opts.isRefresh) {
        try {
          await api.post("/google-sheets/sync-names");
        } catch {
          // No bloquear si falla el sync
        }
        if (isLoadStale(generation)) return;
      }

      let enriched = await resolveSheetNames(campaigns);
      if (isLoadStale(generation)) return;

      enriched = await resolveCalendarNames(enriched);
      if (isLoadStale(generation)) return;

      setItems(enriched);

      if (enriched.length > 0) {
        const accounts = await fetchConnectedAccounts();
        if (isLoadStale(generation)) return;
        await fetchMetaTemplates({
          hasActiveWhatsApp: hasActiveWhatsAppAccount(accounts),
          generation,
        });
      }

      if (opts.isRefresh) {
        if (isLoadStale(generation)) return;
        invalidateAuthenticatedResource("plan-status");
        await loadPlanStatus({ force: true });
      }
    } catch (err) {
      console.error("Error enriching campaign cards:", err);
    }
  };

  const load = async (isRefresh = false, options?: { silent?: boolean }) => {
    const generation = ++loadGenerationRef.current;

    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      // Camino crítico: solo listas de campañas (DB)
      const [renewalsRes, campaignsRes, dynamicsRes, calendarRes] = await fetchAllCampaignLists({
        force: isRefresh,
      });

      if (isLoadStale(generation)) return;

      // Procesar campañas de renovación
      let renewals: RenewalCampaignItem[] = [];
      if (renewalsRes.status === "fulfilled") {
        renewals = (renewalsRes.value.data ?? []).map(item => ({
          ...item,
          google_sheet_id: (item as any).google_sheet_id ?? (item as any).googleSheetId ?? (item as any).sheet_id,
          template_id: (item as any).template_id ?? (item as any).template ?? undefined,
          campaignType: "renewal" as const
        }));
      }

      // Procesar campañas de marketing/broadcast
      let broadcasts: RenewalCampaignItem[] = [];
      if (campaignsRes.status === "fulfilled") {
        broadcasts = (campaignsRes.value.data ?? []).map((item: any) => ({
          id: item.id,
          name: item.name,
          status: item.status,
          created_at: item.created_at,
          updated_at: item.updated_at,
          message_template: item.message_template,
          template_name: item.template_name,
          whatsapp_template_name: item.whatsapp_template_name,
          template_id: item.template_id ?? item.template ?? undefined,
          template_status: item.template_status,
          google_sheet_id: item.google_sheet_id ?? item.googleSheetId ?? item.sheet_id,
          google_sheet_name: item.google_sheet_name,
          messages_sent: item.messages_sent ?? 0,
          messages_delivered: item.messages_delivered ?? 0,
          messages_failed: item.messages_failed ?? 0,
          messages_cancelled: item.messages_cancelled ?? 0,
          campaignType: "marketing" as const
        }));
      }

      // Procesar campañas dinámicas
      let dynamics: RenewalCampaignItem[] = [];
      if (dynamicsRes.status === "fulfilled") {
        dynamics = (dynamicsRes.value.data ?? []).map((item: any) => ({
          id: item.id,
          name: item.name,
          status: item.status,
          created_at: item.created_at,
          updated_at: item.updated_at,
          message_template: item.message_template,
          template_name: item.template_name,
          whatsapp_template_name: item.whatsapp_template_name,
          template_id: item.template_id ?? item.template ?? undefined,
          template_status: item.template_status,
          google_sheet_id: item.google_sheet_id ?? item.googleSheetId ?? item.sheet_id,
          google_sheet_name: item.google_sheet_name,
          messages_sent: item.messages_sent ?? 0,
          messages_delivered: item.messages_delivered ?? 0,
          messages_failed: item.messages_failed ?? 0,
          total_recipients: item.total_recipients ?? 0,
          delivery_rate: item.delivery_rate ?? 0,
          campaignType: "dynamic" as const
        }));
      }

      // Procesar campañas de Google Calendar
      let calendarCampaigns: RenewalCampaignItem[] = [];
      if (calendarRes.status === "fulfilled") {
        calendarCampaigns = (calendarRes.value.data ?? []).map((item: any) => ({
          id: item.id,
          name: item.name,
          status: item.status,
          created_at: item.created_at,
          updated_at: item.updated_at,
          whatsapp_template_name: item.whatsapp_template_name,
          template_name: item.whatsapp_template_name, // fallback for card rendering
          template_id: item.template_id ?? item.template ?? undefined,
          template_status: item.template_status,
          message_template: item.message_template,
          messages_sent: item.messages_sent ?? 0,
          messages_delivered: item.messages_delivered ?? 0,
          messages_failed: item.messages_failed ?? 0,
          calendar_id: item.calendar_id,
          calendar_name: item.calendar_name, // Enriquecido después
          campaignType: "calendar" as const
        }));
      }

      // Combinar todas las listas
      let combined = [...renewals, ...broadcasts, ...dynamics, ...calendarCampaigns];

      // Ordenar por fecha de creación (más recientes primero)
      combined.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      if (isLoadStale(generation)) return;

      // Paint first: cards visibles con datos de DB
      setItems(combined);
      setLoading(false);
      setRefreshing(false);

      if (isRefresh && !options?.silent) {
        toast.success("Campañas actualizadas");
      }

      // Enrich after: sheets / calendar / templates / Meta (mismos endpoints, sin bloquear UI)
      void enrichCampaignCards(combined, generation, { isRefresh });
    } catch (e: any) {
      console.error(e);
      if (!isLoadStale(generation)) {
        toast.error("No se pudieron cargar las campañas");
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (searchParams.get("assistant") === "1") {
      navigate("/campaign-assistant", { replace: true });
      return;
    }
    const highlight = searchParams.get("highlight");
    if (highlight) {
      setHighlightCampaignId(highlight);
      const next = new URLSearchParams(searchParams);
      next.delete("highlight");
      setSearchParams(next, { replace: true });
      setTimeout(() => {
        document.getElementById(`campaign-card-${highlight}`)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 400);
      setTimeout(() => setHighlightCampaignId(null), 4000);
    }
  }, [searchParams, setSearchParams, navigate]);

  // ✅ Detectar retorno de Google Sheets o Google Calendar y abrir el formulario de creación
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromGoogleSheets = urlParams.get('from') === 'google-sheets';
    const fromGoogleCalendar = urlParams.get('from') === 'google-calendar';
    const stepParam = urlParams.get('step');

    if (fromGoogleSheets || fromGoogleCalendar) {
      console.log(
        fromGoogleCalendar
          ? '🔄 Detectado retorno de Google Calendar, abriendo formulario de creación...'
          : '🔄 Detectado retorno de Google Sheets, abriendo formulario de creación...'
      );

      if (fromGoogleCalendar) {
        localStorage.setItem('renewal_create_initial_step', stepParam || 'calendar');
        if (!localStorage.getItem('renewal_create_campaign_type')) {
          localStorage.setItem('renewal_create_campaign_type', 'Calendar');
        }
      } else if (stepParam) {
        localStorage.setItem('renewal_create_initial_step', stepParam);
      }

      preloadAssets();
      setShowCreate(true);
      setHideCreateButton(true);

      urlParams.delete('from');
      urlParams.delete('step');
      const basePath = fromGoogleCalendar ? '/renewal-builder' : window.location.pathname;
      const newUrl = basePath + (urlParams.toString() ? `?${urlParams.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const filtered = items.filter((c) => {
    const q = search.trim().toLowerCase();
    const matchQ =
      !q ||
      c.name.toLowerCase().includes(q) ||
      (c.message_template || "").toLowerCase().includes(q) ||
      (c.google_sheet_name || "").toLowerCase().includes(q);

    // ✅ FIX: El filtro "pending" debe incluir múltiples estados relacionados
    let matchStatus = !statusFilter;
    if (statusFilter === 'pending') {
      // "Pendientes de Activación" incluye: pending, draft, pending_template_approval
      matchStatus = ['pending', 'draft', 'pending_template_approval'].includes(c.status);
    } else if (statusFilter === 'completed') {
      // "Completadas" incluye: completed, completed_with_errors
      matchStatus = ['completed', 'completed_with_errors'].includes(c.status);
    } else if (statusFilter) {
      // Otros filtros: coincidencia exacta
      matchStatus = c.status === statusFilter;
    }

    const matchType = !typeFilter || c.campaignType === typeFilter;
    return matchQ && matchStatus && matchType;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);

  // Helper: iconos profesionales con Lucide
  const getStatusIcon = (status: string) => {
    const iconClass = "h-5 w-5";
    switch (status) {
      case 'draft': return <FileEdit className={`${iconClass} text-gray-500`} />;
      case 'active': return <CheckCircle className={`${iconClass} text-green-500`} />;
      case 'paused': return <PauseCircle className={`${iconClass} text-amber-500`} />;
      case 'completed': return <CheckCircle className={`${iconClass} text-blue-500`} />;
      case 'cancelled': return <XCircle className={`${iconClass} text-gray-400`} />;
      case 'pending_template_approval': return <Hourglass className={`${iconClass} text-orange-500`} />;
      case 'template_rejected': return <AlertCircle className={`${iconClass} text-red-500`} />;
      default: return <FileEdit className={`${iconClass} text-gray-400`} />;
    }
  };

  // Reuse button base to match CampaignList styles
  const btnBase = "h-10 px-3 flex items-center justify-center rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 border";

  // Details modal state
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [detailsCampaign, setDetailsCampaign] = useState<RenewalCampaignItem | null>(null);


  const openDetails = (campaign: RenewalCampaignItem) => {
    setDetailsCampaign(campaign);
    setShowDetailsModal(true);
  };

  const closeDetails = () => {
    setDetailsCampaign(null);
    setShowDetailsModal(false);
  };

  const updateStatus = async (id: string, status: RenewalCampaignItem["status"]) => {
    try {
      await api.put(`/renewals/${id}/status`, { status });
      setItems((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
      toast.success("Estado actualizado");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo actualizar estado");
    }
  };

  const deleteCampaign = async (id?: string) => {
    if (!id && !deleteTarget) return;
    const campaignId = id || deleteTarget!.id;

    // Determinar el endpoint correcto basado en el tipo de campaña
    const campaign = items.find(item => item.id === campaignId);
    let endpoint: string;
    if (campaign?.campaignType === "marketing") {
      endpoint = `/campaigns/${campaignId}`;
    } else if (campaign?.campaignType === "dynamic") {
      endpoint = `/dynamics/${campaignId}`;
    } else if (campaign?.campaignType === "calendar") {
      endpoint = `/calendar-campaigns/${campaignId}`;
    } else {
      endpoint = `/renewals/${campaignId}`;
    }

    try {
      await api.delete(endpoint);
      setItems((prev) => prev.filter((p) => p.id !== campaignId));
      toast.success("Campaña eliminada");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo eliminar");
    } finally {
      closeDeleteConfirm();
    }
  };

  // Skeleton loader para cards de campaña
  if (loading) {
    return (
      <div className="max-w-[1600px] mx-auto">
        <div className="space-y-6 animate-fadeIn">
          {/* Header skeleton */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <div className="h-8 w-40 bg-gray-200 rounded-lg animate-pulse"></div>
              <div className="h-4 w-64 bg-gray-100 rounded mt-2 animate-pulse"></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gray-200 rounded-xl animate-pulse"></div>
              <div className="w-48 h-11 bg-gray-200 rounded-xl animate-pulse"></div>
            </div>
          </div>

          {/* Search bar skeleton */}
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-6">
            <div className="flex gap-3 items-center">
              <div className="flex-1 h-12 bg-gray-100 rounded-md animate-pulse"></div>
              <div className="w-48 h-12 bg-gray-100 rounded-md animate-pulse"></div>
            </div>
          </div>

          {/* Stats cards skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[...Array(5)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="h-4 w-20 bg-gray-200 rounded"></div>
                      <div className="h-8 w-12 bg-gray-100 rounded mt-2"></div>
                    </div>
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Campaign cards skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="h-full animate-pulse">
                <CardContent className="p-6">
                  {/* Header skeleton */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-xl"></div>
                      <div>
                        <div className="h-5 w-32 bg-gray-200 rounded"></div>
                        <div className="h-4 w-20 bg-gray-100 rounded mt-1"></div>
                        <div className="h-3 w-40 bg-gray-100 rounded mt-1"></div>
                      </div>
                    </div>
                    <div className="h-6 w-16 bg-gray-200 rounded-md"></div>
                  </div>

                  {/* Template section skeleton */}
                  <div className="p-3 bg-gray-50 rounded-xl mb-4">
                    <div className="h-3 w-16 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 w-full bg-gray-100 rounded"></div>
                    <div className="h-3 w-3/4 bg-gray-100 rounded mt-1"></div>
                  </div>

                  {/* Buttons skeleton */}
                  <div className="grid grid-cols-2 gap-3 mt-auto">
                    <div className="h-10 bg-gray-100 rounded-lg"></div>
                    <div className="h-10 bg-gray-100 rounded-lg"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const total = items.length;
  const activeCount = items.filter(i => (i as any).status === "active").length;
  const cancelledCount = items.filter(i => (i as any).status === "cancelled").length;
  const completedCount = items.filter(i => (i as any).status === "completed").length;
  // Contar campañas pendientes de aprobación de template
  const pendingTemplateCount = items.filter(i =>
    (i as any).status === "pending_template_approval"
  ).length;
  // ✅ FIX: Solo contar campañas con estado "pending" real, no las que tienen processing_status="pending"
  // processing_status="pending" es normal para campañas activas que aún no han procesado mensajes
  const pendingCount = items.filter(i =>
    (i as any).status === "pending" || (i as any).status === "draft"
  ).length + pendingTemplateCount;

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="space-y-6 animate-fadeIn">
        {/* Header - oculto mientras se está creando una campaña */}
        {!showCreate && (
          <div className="flex flex-col space-y-4">
            {/* Título y botones */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Campañas</h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1 leading-relaxed">
                  Gestiona tus campañas de Marketing y Recordatorios Periódicos.
                </p>
              </div>
              <div className="grid grid-cols-[44px_minmax(0,1fr)] gap-3 w-full sm:flex sm:w-auto sm:items-center sm:justify-end">
                {!hideCreateButton && (
                  <>
                    <Button
                      onClick={() => load(true)}
                      disabled={refreshing}
                      variant="outline"
                      className="bg-white hover:bg-slate-50 border-gray-200 text-gray-500 w-11 h-11 p-0 rounded-xl shadow-sm transition-all active:scale-95 group"
                    >
                      <RotateCcw className={`w-5 h-5 transition-transform duration-500 ${refreshing ? 'animate-spin' : 'group-hover:rotate-180'}`} />
                    </Button>

                    <Button
                      id="tour-new-campaign-btn"
                      onClick={async () => {
                        // Verificar si WhatsApp está conectado antes de crear campaña
                        const isConnected = await checkWhatsAppConnection();

                        if (!isConnected) {
                          toast.error("WhatsApp no conectado", {
                            description: "Antes de crear una campaña, por favor conecta tu cuenta de WhatsApp"
                          });
                          // Redirigir a la página de WhatsApp después de un breve delay para que se vea el toast
                          setTimeout(() => {
                            navigate('/whatsapp');
                          }, 1500);
                          return;
                        }

                        // ✅ Pre-cargar assets INMEDIATAMENTE cuando hace clic
                        preloadAssets();
                        // abrir formulario de creación localmente y ocultar el botón
                        setShowCreate(true);
                        setHideCreateButton(true);
                      }}
                      className="w-full min-w-0 bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white px-2.5 sm:px-6 h-10 sm:h-11 rounded-xl hover:shadow-lg flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base font-semibold transition-all active:scale-95 sm:w-auto"
                    >
                      <Plus className="w-5 h-5" />
                      Crea una nueva campaña
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => navigate("/campaign-assistant")}
                      className="col-span-2 w-full h-10 sm:h-11 rounded-xl border-teal-200 text-teal-700 hover:bg-teal-50 flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base font-semibold sm:col-auto sm:w-auto"
                    >
                      <Sparkles className="w-4 h-4" />
                      Asistente Reveca
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Meta account restricted/blocked — above plan info */}
            <MetaAccountHealthBanner accounts={criticalAccounts} />

            {/* Información del Plan */}
            {!loadingPlan && !planStatus && planError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 shadow-sm flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-red-800">No se pudo cargar la información del plan</p>
                    <p className="text-xs text-red-600">{planError}</p>
                  </div>
                </div>
                <Button 
                  onClick={() => loadPlanStatus({ force: true })}
                  variant="outline" 
                  size="sm"
                  className="bg-white border-red-200 text-red-700 hover:bg-red-100 h-8"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  Reintentar
                </Button>
              </div>
            )}

            {!loadingPlan && planStatus && (
              <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 sm:p-4 shadow-sm transition-all duration-300 ${(planStatus.is_blocked || planStatus.plan === 'blocked') ? '' : isPlanExpanded ? '' : 'hover:shadow-md'}`}>
                
                {planStatus.is_blocked || planStatus.plan === 'blocked' ? (
                  <div className="flex flex-col items-center justify-center p-4 text-center">
                    <AlertCircle className="h-10 w-10 text-red-500 mb-3" />
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      {planStatus.trial_expired ? 'Periodo de prueba expirado' :
                        planStatus.subscription_expired ? 'Suscripción expirada' :
                        'No tienes un plan activo o límite alcanzado'}
                    </h3>
                    <p className="text-sm text-gray-600 mb-5 max-w-md mx-auto">
                      Adquiere un plan para continuar reactivando tus campañas y seguir enviando mensajes a tus clientes.
                    </p>
                    <button
                      onClick={() => navigate('/payment')}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                    >
                      <Zap className="h-5 w-5" />
                      Ver planes y suscribirse
                    </button>
                  </div>
                ) : (
                  <>
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setIsPlanExpanded(!isPlanExpanded)}
                    >
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-blue-600" />
                        <h3 className="text-base font-bold text-gray-900">Información del Plan</h3>
                        {planStatus.subscription && planStatus.prepaid && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded">
                            Dual
                          </span>
                        )}
                      </div>
                      <div className="text-blue-600">
                        {isPlanExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </div>
                    </div>

                    <div className={`space-y-0 text-sm overflow-hidden transition-all duration-300 ${isPlanExpanded ? 'max-h-[32rem] mt-3 opacity-100' : 'max-h-0 mt-0 opacity-0'}`}>
                      {planStatus.subscription && planStatus.prepaid ? (
                        <>
                          <p className="text-xs text-blue-800/80 px-3 pb-2">
                            Usando {planStatus.active_bucket === 'prepaid' ? 'prepago' : 'suscripción'} primero.
                            La suscripción tiene prioridad mientras tenga cupo del mes.
                          </p>
                          <div className="space-y-2 px-1 pb-2">
                            <div className={`rounded-lg border px-3 py-2.5 bg-white ${planStatus.active_bucket === 'subscription' ? 'border-blue-300 ring-1 ring-blue-200' : 'border-gray-200'}`}>
                              <div className="flex justify-between items-center gap-2">
                                <span className="font-semibold text-gray-900">
                                  Suscripción: {getPlanDisplayName(planStatus.subscription.plan_type || planStatus.plan)}
                                  {planStatus.active_bucket === 'subscription' && (
                                    <span className="ml-2 text-[10px] font-bold uppercase text-blue-600">Activo</span>
                                  )}
                                </span>
                                <span className="font-semibold text-blue-600">
                                  {planStatus.subscription.remaining ?? 0} / {planStatus.subscription.limit ?? 0} msgs
                                </span>
                              </div>
                              {(planStatus.subscription.limit ?? 0) > 0 && (
                                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-blue-500"
                                    style={{
                                      width: `${Math.min(100, ((planStatus.subscription.usage ?? 0) / (planStatus.subscription.limit || 1)) * 100)}%`,
                                    }}
                                  />
                                </div>
                              )}
                              {planStatus.subscription.expires_at && (
                                <p className="mt-1.5 text-[11px] text-gray-500">
                                  Ciclo hasta{' '}
                                  {new Date(planStatus.subscription.expires_at).toLocaleDateString('es-ES', {
                                    day: 'numeric', month: 'short', year: 'numeric',
                                  })}
                                  {planStatus.subscription.period_expired ? ' · vencida' : ''}
                                </p>
                              )}
                            </div>
                            <div className={`rounded-lg border px-3 py-2.5 bg-white ${planStatus.active_bucket === 'prepaid' ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-gray-200'}`}>
                              <div className="flex justify-between items-center gap-2">
                                <span className="font-semibold text-gray-900">
                                  Prepago: Postpago
                                  {planStatus.active_bucket === 'prepaid' && (
                                    <span className="ml-2 text-[10px] font-bold uppercase text-emerald-600">Activo</span>
                                  )}
                                </span>
                                <span className="font-semibold text-emerald-600">
                                  {planStatus.prepaid.remaining ?? 0} / {planStatus.prepaid.limit ?? 0} msgs
                                </span>
                              </div>
                              {(planStatus.prepaid.limit ?? 0) > 0 && (
                                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-emerald-500"
                                    style={{
                                      width: `${Math.min(100, ((planStatus.prepaid.usage ?? 0) / (planStatus.prepaid.limit || 1)) * 100)}%`,
                                    }}
                                  />
                                </div>
                              )}
                              {typeof planStatus.prepaid.balance_amount === 'number' && (
                                <p className="mt-1.5 text-[11px] text-gray-500">
                                  Saldo ~ ${Math.round(planStatus.prepaid.balance_amount).toLocaleString('es-CO')} COP
                                </p>
                              )}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between items-center py-2 px-3 rounded-t-lg bg-white">
                            <span className="text-gray-600">Plan:</span>
                            <span className={`font-semibold ${planStatus.plan === 'unlimited' ? 'text-green-600' :
                              planStatus.plan === 'blocked' ? 'text-red-600' :
                                'text-blue-600'
                              }`}>
                              {getPlanDisplayName(planStatus.plan)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2 px-3 bg-gray-100">
                            <span className="text-gray-600">Mensajes entregados (plan):</span>
                            <span className="font-semibold text-gray-900">{planStatus.usage}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 px-3 bg-white">
                            <span className="text-gray-600">Mensajes disponibles:</span>
                            <span className={`font-semibold ${planStatus.remaining === null ? 'text-green-600' :
                              planStatus.remaining <= 0 ? 'text-red-600' :
                                planStatus.remaining < 100 ? 'text-yellow-600' :
                                  'text-blue-600'
                              }`}>
                              {planStatus.remaining === null ? '∞' : planStatus.remaining}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2 px-3 bg-gray-100">
                            <span className="text-gray-600">Límite del plan:</span>
                            <span className="font-semibold text-gray-900">
                              {planStatus.limit === null ? '∞' : planStatus.limit}
                            </span>
                          </div>
                          {planStatus.prepaid && !planStatus.subscription && typeof planStatus.prepaid.balance_amount === 'number' && (
                            <div className="flex justify-between items-center py-2 px-3 bg-white">
                              <span className="text-gray-600">Saldo prepago:</span>
                              <span className="font-semibold text-emerald-600">
                                ${Math.round(planStatus.prepaid.balance_amount).toLocaleString('es-CO')} COP
                              </span>
                            </div>
                          )}
                          {planStatus.plan !== 'prepaid' && (
                            <div className="flex justify-between items-center py-2 px-3 rounded-b-lg bg-white">
                              <div className="flex items-center gap-1.5">
                                <span className="text-gray-600">Ciclo actual:</span>
                                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                                  Restan {Math.ceil((new Date(planStatus.billing_cycle_end).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} días
                                </span>
                              </div>
                              <div className="text-right">
                                <div className="font-medium text-blue-600 text-xs">
                                  {new Date(planStatus.billing_cycle_start).toLocaleDateString('es-ES', {
                                    day: 'numeric', month: 'short'
                                  })} - {new Date(planStatus.billing_cycle_end).toLocaleDateString('es-ES', {
                                    day: 'numeric', month: 'short', year: 'numeric'
                                  })}
                                </div>
                              </div>
                            </div>
                          )}

                          {planStatus.limit !== null && (
                            <div className="pt-2 px-3">
                              <div className="flex items-center gap-3">
                                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full transition-all ${planStatus.remaining <= 0 ? 'bg-red-500' :
                                      planStatus.remaining < 100 ? 'bg-yellow-500' :
                                        'bg-green-500'
                                      }`}
                                    style={{ width: `${Math.min(100, (planStatus.usage / planStatus.limit) * 100)}%` }}
                                  />
                                </div>
                                <span className={`text-sm font-bold min-w-[45px] text-right ${(planStatus.usage / planStatus.limit) * 100 >= 80 ? 'text-red-600' :
                                  (planStatus.usage / planStatus.limit) * 100 >= 50 ? 'text-yellow-600' :
                                    'text-green-600'
                                  }`}>
                                  {Math.round((planStatus.usage / planStatus.limit) * 100)}%
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Mostrar formulario de creación cuando el usuario lo solicite */}
        {showCreate && (
          <div className="mt-6">
            <RenewalCreate
              onCancel={() => {
                // cerrar formulario y restaurar el botón
                setShowCreate(false);
                setHideCreateButton(false);
                setPreCachedAssets(null);  // ✅ Limpiar cache
              }}
              onCreated={(id?: string) => {
                // cerrar formulario, restaurar el botón y recargar lista
                setShowCreate(false);
                setHideCreateButton(false);
                setPreCachedAssets(null);  // ✅ Limpiar cache
                void load(true, { silent: true });
                // si hay callback externo, no hacemos nada extra (onCreateNew ya fue llamado)
              }}
              preCachedAssets={preCachedAssets}
            />
          </div>
        )}

        {/* Si el formulario de creación está abierto, ocultamos el resto de la UI */}
        {showCreate ? null : (
          <>
            {/* Search / Filters - borde siempre visible pero sutil */}
            <div className="mb-6">
              {/* Ajuste: padding vertical igual arriba/abajo para simetría */}
              <div className="bg-white border border-gray-200 rounded-xl px-4 py-6">
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                  <div className="relative w-full min-w-0 sm:flex-1">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      className="w-full pl-14 pr-4 py-3 rounded-md text-sm bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-[#1CB3B3] transition-colors"
                      placeholder="Buscar por nombre o texto de plantilla..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                  </div>
                  <div className="w-full sm:w-48">
                    <ColumnSelect
                      label=""
                      placeholder="Todas las campañas"
                      className="py-3"
                      options={["Todas las campañas", "Marketing", "Recordatorios Periódicos", "Dinámica", "Google Calendar"]}
                      value={!typeFilter ? "Todas las campañas" : typeFilter === "marketing" ? "Marketing" : typeFilter === "renewal" ? "Recordatorios Periódicos" : typeFilter === "calendar" ? "Google Calendar" : "Dinámica"}
                      onChange={(val) => {
                        if (val === "Todas las campañas") setTypeFilter("");
                        else if (val === "Marketing") setTypeFilter("marketing");
                        else if (val === "Recordatorios Periódicos") setTypeFilter("renewal");
                        else if (val === "Dinámica") setTypeFilter("dynamic");
                        else if (val === "Google Calendar") setTypeFilter("calendar");
                        setPage(1);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Stats cards: mismo tamaño y distribución */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              <Card className={`cursor-pointer hover:shadow-lg transition ${!statusFilter ? 'ring-2 ring-[#e6f7f4]' : ''}`} onClick={() => { setStatusFilter(''); setPage(1); }}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total</p>
                      <p className="text-2xl font-bold text-[#1fb6b4]">{total}</p>
                    </div>
                    <div className="w-8 h-8 bg-[#E8F7F7] rounded-lg flex items-center justify-center">
                      <BarChart3 className="h-4 w-4 text-[#1CB3B3]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={`cursor-pointer hover:shadow-lg transition ${statusFilter === 'active' ? 'ring-2 ring-green-200' : ''}`} onClick={() => { setStatusFilter('active'); setPage(1); }}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Activas</p>
                      <p className="text-2xl font-bold text-green-600">{activeCount}</p>
                    </div>
                    <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
                      <Send className="h-4 w-4 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={`cursor-pointer hover:shadow-lg transition ${statusFilter === 'completed' ? 'ring-2 ring-blue-200' : ''}`} onClick={() => { setStatusFilter('completed'); setPage(1); }}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Completadas</p>
                      <p className="text-2xl font-bold text-blue-600">{completedCount}</p>
                    </div>
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={`cursor-pointer hover:shadow-lg transition ${statusFilter === 'pending' ? 'ring-2 ring-amber-200' : ''}`} onClick={() => { setStatusFilter('pending'); setPage(1); }}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Pendientes de Activación</p>
                      <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
                    </div>
                    <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
                      <RotateCcw className="h-4 w-4 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={`cursor-pointer hover:shadow-lg transition ${statusFilter === 'cancelled' ? 'ring-2 ring-red-200' : ''}`} onClick={() => { setStatusFilter('cancelled'); setPage(1); }}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Canceladas</p>
                      <p className="text-2xl font-bold text-red-600">{cancelledCount}</p>
                    </div>
                    <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                      <XCircle className="h-4 w-4 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* List / empty state - igual funcionalidad */}
            {filtered.length === 0 ? (
              <Card className="border-2 border-dashed border-gray-200">
                <CardContent className="py-12 text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] rounded-full mx-auto mb-4 flex items-center justify-center text-white"><Megaphone className="h-7 w-7" /></div>
                  <h3 className="text-lg font-medium">{search ? "No se encontraron campañas" : "No hay campañas de recordatorios periódicos aún"}</h3>
                  <p className="text-sm text-gray-500 mt-2">{search ? "Ajusta los filtros." : "Crea tu primera campaña de recordatorios periódicos para automatizar tus mensajes."}</p>
                  <div className="mt-4 flex flex-col sm:flex-row justify-center gap-2">
                    <Button variant="outline" onClick={() => { setSearch(""); setStatusFilter(""); setPage(1); }} className="w-full sm:w-auto gap-1.5"><RotateCcw className="h-4 w-4" /> Limpiar filtros</Button>
                    {!hideCreateButton && (
                      <Button
                        onClick={async () => {
                          const isConnected = await checkWhatsAppConnection();
                          if (!isConnected) {
                            toast.error("WhatsApp no conectado", {
                              description: "Antes de crear una campaña, por favor conecta tu cuenta de WhatsApp"
                            });
                            setTimeout(() => { navigate('/whatsapp'); }, 1500);
                            return;
                          }
                          setShowCreate(true);
                          setHideCreateButton(true);
                        }}
                        className={`w-full sm:w-auto bg-gradient-to-r ${LORO_FROM} ${LORO_TO} text-white`}
                      >
                        <Megaphone className="h-4 w-4 mr-1.5" /> Crear Campaña
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
                {pageItems.map((c) => (
                  <Card
                    key={c.id}
                    id={`campaign-card-${c.id}`}
                    className={`h-full flex flex-col group relative bg-white hover:shadow-2xl transition-all duration-300 rounded-xl border shadow-md ${
                      highlightCampaignId === c.id
                        ? "border-teal-500 ring-2 ring-teal-400/60"
                        : "border-gray-200/60"
                    }`}
                  >
                    <CardContent className="flex flex-col h-full p-6">
                      {/* Header like CampaignCard */}
                      <div className="flex items-start gap-3 mb-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 bg-[#E8F7F7] border border-[#1CB3B3]/30 rounded-xl flex items-center justify-center shrink-0">
                            {getStatusIcon(c.status)}
                          </div>

                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-gray-900 leading-snug break-words">
                              {c.name}
                            </h3>

                            {/* Badge de tipo de campaña */}
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#E8F7F7] text-[#0D7377] border border-[#2ab5a4]/30">
                              {c.campaignType === "marketing"
                                ? <><Megaphone className="h-3 w-3" /> Marketing</>
                                : c.campaignType === "dynamic"
                                  ? <><Zap className="h-3 w-3" /> Dinámica</>
                                  : c.campaignType === "calendar"
                                    ? <><Calendar className="h-3 w-3" /> Google Calendar</>
                                    : <><RotateCcw className="h-3 w-3" /> Recordatorios Periódicos</>}
                            </span>

                            <p className="text-sm text-gray-500 break-words">
                              {c.campaignType === "calendar"
                                ? <><span className="font-medium">Calendario:</span> {(c as any).calendar_name ?? "Google Calendar"}</>
                                : <><span className="font-medium">Hoja:</span> {c.google_sheet_name ?? "Sin hoja vinculada"}</>
                              }
                            </p>
                          </div>
                        </div>

                        <div className="shrink-0">
                          <Badge
                            variant="secondary"
                            className={`${(statusConfig[c.status] || statusConfig.draft).color} border px-3 py-1 rounded-md whitespace-nowrap`}
                          >
                            {(statusConfig[c.status] || { label: c.status }).label}
                          </Badge>
                        </div>
                      </div>

                      {/* Stats: Enviados, Entregados (facturados), Fallidos */}
                      <div className="grid grid-cols-3 gap-2 mb-4 opacity-0 max-h-0 overflow-hidden group-hover:opacity-100 group-hover:max-h-28 transition-all duration-300 ease-in-out">
                        <div className="border border-gray-200 rounded-lg p-3 bg-white text-center">
                          <div className="text-2xl font-bold text-blue-600">{c.messages_sent ?? 0}</div>
                          <div className="text-xs text-gray-600 mt-1">Enviados</div>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-3 bg-white text-center">
                          <div className="text-2xl font-bold text-green-600">{c.messages_delivered ?? 0}</div>
                          <div className="text-xs text-gray-600 mt-1">Entregados</div>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-3 bg-white text-center">
                          <div className="text-2xl font-bold text-red-600">{(c as any).messages_failed ?? 0}</div>
                          <div className="text-xs text-gray-600 mt-1">Fallidos</div>
                        </div>
                      </div>

                      {/* Banner de estado pendiente de aprobación */}
                      {c.status === "pending_template_approval" && (
                        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <Hourglass className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <div className="text-sm font-medium text-orange-800">Esperando aprobación de Meta</div>
                              <div className="text-xs text-orange-600 mt-1">
                                La campaña se activará automáticamente cuando Meta apruebe la plantilla.
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Banner de template rechazado */}
                      {c.status === "template_rejected" && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <div className="text-sm font-medium text-red-800">Plantilla rechazada por Meta</div>
                              <div className="text-xs text-red-600 mt-1">
                                Edita la campaña para usar una plantilla aprobada o crear una nueva.
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {(() => {
                        const anyc = c as any;
                        const templatePreview = resolveTemplatePreview({
                          templateName: c.whatsapp_template_name || c.template_name,
                          templateId: c.template_id ?? anyc.template ?? anyc.templateId,
                          messageTemplateFallback: c.message_template,
                          templatesMap: metaTemplatesMap,
                          campaignFields: {
                            header_type: anyc.header_type,
                            header_text: anyc.header_text,
                            header_image_url: anyc.header_image_url,
                            footer_text: anyc.footer_text,
                            buttons: anyc.buttons,
                          },
                        });
                        if (!templatePreview.name) {
                          templatePreview.name =
                            formatTemplateName(c.whatsapp_template_name || c.template_name) || "Sin nombre";
                        }
                        return (
                          <>
                            <TemplatePreviewPanel
                              preview={templatePreview}
                              showTemplateName={true}
                            />
                            {c.template_status && c.template_status !== "APPROVED" && (
                              <div
                                className={`text-xs mb-4 -mt-2 ${c.template_status === "PENDING" ? "text-orange-600" : "text-red-600"}`}
                              >
                                Estado:{" "}
                                {c.template_status === "PENDING"
                                  ? "Pendiente"
                                  : c.template_status === "REJECTED"
                                  ? "Rechazada"
                                  : c.template_status}
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {/* Botones de acción */}
                      <div className="grid grid-cols-2 gap-3 mt-auto">
                        <Button
                          size="sm"
                          className={`${btnBase} bg-[#E8F7F7] text-[#0D7377] border-[#1CB3B3]/30 hover:bg-[#D5F0F0] hover:border-[#1CB3B3]/50 shadow-sm`}
                          onClick={() => openDetails(c)}
                          aria-label="Detalles"
                        >
                          <Eye className="h-4 w-4 mr-1.5" /> Detalles
                        </Button>

                        {isPauseableType(c.campaignType) && ["active", "running", "scheduled"].includes(c.status as string) ? (
                          <Button
                            size="sm"
                            className={`${btnBase} bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 hover:border-amber-300 shadow-sm`}
                            onClick={() => openPauseConfirm(c)}
                            aria-label="Pausar"
                          >
                            <PauseCircle className="h-4 w-4 mr-1.5" /> Pausar
                          </Button>
                        ) : isPauseableType(c.campaignType) && c.status === "paused" ? (
                          <Button
                            size="sm"
                            className={`${btnBase} bg-[#E8F7F7] text-[#0D7377] border-[#1CB3B3]/30 hover:bg-[#D5F0F0] hover:border-[#1CB3B3]/50 shadow-sm`}
                            onClick={() => resumeCampaign(c)}
                            disabled={resumingId === c.id}
                            aria-label="Activar"
                          >
                            {resumingId === c.id ? (
                              <div className="w-4 h-4 border-2 border-[#0D7377] border-t-transparent rounded-full animate-spin mr-1.5" />
                            ) : (
                              <Play className="h-4 w-4 mr-1.5" />
                            )}
                            Activar
                          </Button>
                        ) : ["active", "pending", "scheduled", "pending_template_approval", "draft"].includes(c.status) ? (
                          <Button
                            size="sm"
                            className={`${btnBase} bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100 hover:border-rose-300 shadow-sm`}
                            onClick={() => openCancelConfirm(c)}
                            aria-label="Cancelar"
                          >
                            <XCircle className="h-4 w-4 mr-1.5" /> Cancelar
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className={`${btnBase} bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100 hover:border-rose-300 shadow-sm`}
                            onClick={() => openDeleteConfirm(c)}
                            aria-label="Eliminar"
                          >
                            <Trash2 className="h-4 w-4 mr-1.5" /> Eliminar
                          </Button>
                        )}

                        {isPauseableType(c.campaignType) && c.status === "paused" && (
                          <Button
                            size="sm"
                            className={`${btnBase} col-span-2 bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100 hover:border-rose-300 shadow-sm`}
                            onClick={() => openCancelConfirm(c)}
                            aria-label="Cancelar"
                          >
                            <XCircle className="h-4 w-4 mr-1.5" /> Cancelar
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Delete Confirmation Modal (separado) */}
        <RenewalDeleteConfirm
          open={showDeleteModal}
          target={deleteTarget}
          onClose={closeDeleteConfirm}
          onConfirm={deleteCampaign}
        />

        {showPauseModal && pauseTarget && (
          <div className="fixed inset-0 z-[99999] bg-black bg-opacity-40 flex items-center justify-center" role="dialog" aria-modal="true">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full overflow-hidden mx-4">
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                    <PauseCircle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Pausar campaña</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      ¿Deseas pausar la campaña <span className="font-semibold">"{pauseTarget.name}"</span>?
                    </p>
                    <p className="mt-2 text-xs text-amber-700 bg-amber-50 rounded-lg p-2 border border-amber-200">
                      Se detendrán los recordatorios pendientes. Podrás reactivarla después; solo se programarán los futuros.
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-4 pb-4">
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={closePauseConfirm} className="px-4" disabled={pausing}>
                    Volver
                  </Button>
                  <Button
                    className="bg-amber-600 hover:bg-amber-700 text-white px-4"
                    onClick={pauseCampaign}
                    disabled={pausing}
                  >
                    {pausing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Pausando...
                      </>
                    ) : (
                      "Sí, pausar"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ✅ NUEVO: Cancel Confirmation Modal */}
        {showCancelModal && cancelTarget && (
          <div className="fixed inset-0 z-[99999] bg-black bg-opacity-40 flex items-center justify-center" role="dialog" aria-modal="true">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full overflow-hidden mx-4">
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center">
                    <XCircle className="h-5 w-5 text-rose-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Cancelar campaña</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      ¿Estás seguro de que deseas cancelar la campaña <span className="font-semibold">"{cancelTarget.name}"</span>?
                    </p>
                    <p className="mt-2 text-xs text-rose-600 bg-rose-50 rounded-lg p-2 border border-rose-200">
                      ⚠️ Esta acción eliminará todos los mensajes pendientes de envío y la campaña ya no procesará más mensajes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-4 pb-4">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={closeCancelConfirm}
                    className="px-4"
                    disabled={cancelling}
                  >
                    Volver
                  </Button>
                  <Button
                    className="bg-rose-600 hover:bg-rose-700 text-white px-4"
                    onClick={cancelCampaign}
                    disabled={cancelling}
                  >
                    {cancelling ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Cancelando...
                      </>
                    ) : (
                      "Sí, cancelar campaña"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Renewal details modal */}
        <RenewalDetailsModal open={showDetailsModal} campaign={detailsCampaign} onClose={closeDetails} />


        {/* Paginación - Consistente con MessageTemplates */}
        {!showCreate && totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-600">
              Página {page} de {totalPages} — mostrando {pageItems.length} de {filtered.length}
            </div>

            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                Anterior
              </Button>

              <div className="hidden sm:flex items-center gap-1">
                {Array.from({ length: totalPages }).map((_, i) => {
                  const pageNum = i + 1;
                  const isActive = pageNum === page;
                  // Mostrar máximo 5 páginas para no saturar
                  if (totalPages <= 7 || pageNum === 1 || pageNum === totalPages || (pageNum >= page - 1 && pageNum <= page + 1)) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${isActive ? 'bg-[#1CB3B3] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === page - 2 || pageNum === page + 2) {
                    return <span key={pageNum} className="px-1 text-gray-400">...</span>;
                  }
                  return null;
                })}
              </div>

              <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RenewalCampaigns;